import {
  BookOpenIcon,
  CalendarIcon,
  ChatIcon,
  ClipboardCheckIcon,
  CurrencyDollarIcon,
  CursorClickIcon,
  GlobeAltIcon,
  IdentificationIcon,
  MailIcon,
  NewspaperIcon,
  PresentationChartLineIcon,
  ScaleIcon,
  ShieldCheckIcon,
  VideoCameraIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import type { IndustryContent } from '../types';
import LeadsBoard from '../visuals/LeadsBoard';
import { tone } from '../../primitives';

const BRAND_BG = 'bg-[#c4501d]';

export function useSalesContent(): IndustryContent {
  const { t } = useLocale('bot-use-cases');
  const { t: tc } = useLocale('chatbots');

  return {
    key: 'sales',
    seoTitle: t('AI Sales Agent: Lead Generation Chatbot That Books Demos'),
    seoDescription: t(
      'Turn website visitors into booked meetings. An AI sales agent answers pricing questions, qualifies leads and books demos into your calendar, around the clock.',
    ),
    hero: {
      label: t('AI sales agent'),
      title: t('A sales agent for every visitor.'),
      accent: t('Every good lead gets a meeting.'),
      lead: t(
        'Your agent answers product and pricing questions the moment they come up, asks the questions your team would ask, and books qualified visitors straight into your calendar.',
      ),
      description: t(
        'Example: a visitor asks a software company about a team plan, and the agent finds a free time and books a demo in Google Calendar.',
      ),
      surface: 'widget',
      brand: {
        name: 'Corvo Analytics',
        initial: 'C',
        kind: t('Analytics software'),
        bg: BRAND_BG,
      },
      messages: [
        { from: 'visitor', text: tc('Do you have a plan for teams?'), at: 600 },
        {
          from: 'agent',
          typingAt: 1100,
          at: 1900,
          content: tc(
            'Yes, from five seats. Shall I book you a 15-minute demo this week?',
          ),
        },
        {
          from: 'visitor',
          text: t('Sure, Thursday afternoon works.'),
          at: 2900,
        },
        {
          from: 'agent',
          typingAt: 3300,
          at: 4100,
          content: <SlotPicker />,
        },
        {
          from: 'agent',
          at: 4900,
          content: t(
            'You’re booked for Thursday at 3:30 with Sam from our team.',
          ),
        },
      ],
      steps: [
        {
          icon: BookOpenIcon,
          title: t('Answered from the pricing page'),
          via: tc('Knowledge'),
          at: 1400,
        },
        {
          icon: CalendarIcon,
          title: t('Found free times on Thursday'),
          via: tc('Google Calendar'),
          at: 3600,
        },
        {
          icon: CalendarIcon,
          title: t('Booked the demo'),
          via: tc('Google Calendar'),
          at: 4700,
        },
      ],
    },
    jobs: {
      title: t('Every step a good rep would take.'),
      tail: t('For every visitor, at any hour.'),
      lead: t(
        'Your agent follows your sales playbook. It answers questions, asks the ones your team would ask and books the right people in, so your reps start every call with the details they need.',
      ),
      items: [
        {
          icon: CurrencyDollarIcon,
          title: t('Pricing questions'),
          detail: t(
            'Explains plans, limits and discounts straight from your pricing page.',
          ),
        },
        {
          icon: ClipboardCheckIcon,
          title: t('Lead qualification'),
          detail: t(
            'Asks your qualifying questions, such as team size or timeline, and saves the answers.',
          ),
        },
        {
          icon: CalendarIcon,
          title: t('Demo booking'),
          detail: t(
            'Finds a free slot in Google Calendar and books it while the visitor is still on your site.',
          ),
        },
        {
          icon: ScaleIcon,
          title: t('Plan comparisons'),
          detail: t(
            'Explains which plan fits and how you compare, from the content you provide.',
          ),
        },
        {
          icon: ShieldCheckIcon,
          title: t('Common objections'),
          detail: t(
            'Answers questions about security, setup and contracts from your own documents.',
          ),
        },
        {
          icon: CursorClickIcon,
          title: t('Next steps'),
          detail: t(
            'Shows a button to your signup, checkout or pricing page at the right moment.',
          ),
        },
      ],
    },
    workflow: {
      title: t('Trained on your pitch.'),
      tail: t('Connected to your calendar.'),
      learns: [
        { icon: GlobeAltIcon, label: t('Website and pricing page') },
        {
          icon: PresentationChartLineIcon,
          label: t('Sales decks and one-pagers'),
        },
        { icon: NewspaperIcon, label: t('Case studies') },
        { icon: VideoCameraIcon, label: t('Product videos') },
      ],
      acts: [
        { icon: CalendarIcon, label: tc('Google Calendar') },
        { icon: IdentificationIcon, label: tc('Collect data') },
        { icon: CursorClickIcon, label: tc('Buttons') },
        { icon: MailIcon, label: tc('Email') },
      ],
      channels: [
        { icon: ChatIcon, label: tc('Website chat') },
        { icon: 'FaWhatsapp', label: 'WhatsApp' },
        { icon: 'FaFacebookMessenger', label: 'Messenger' },
        { icon: 'FaLine', label: 'LINE' },
        { icon: 'FaPhone', label: tc('Phone calls') },
      ],
    },
    spotlight: {
      title: t('Leads that arrive ready.'),
      tail: t('Not just an email address.'),
      lead: t(
        'Every lead comes with the conversation behind it, so your team knows what the visitor wants before the first call.',
      ),
      points: [
        t('An email for every new lead'),
        t('The answers to your qualifying questions'),
        t('Export leads as CSV for your CRM'),
        t('Send each lead to a webhook as it comes in'),
      ],
      description: t(
        'Example: the leads list with the newest lead, the answers to a qualifying question and a button to export them as CSV, next to the email the team gets for a new lead.',
      ),
      visual: <LeadsBoard />,
    },
    faqs: [
      {
        question: t('Can it book meetings in my calendar?'),
        answer: t(
          'Yes. Connect Google Calendar and the agent checks your free times and books the meeting while the visitor is still chatting. You set the meeting length, title and time zone.',
        ),
      },
      {
        question: t('How does it qualify leads?'),
        answer: t(
          'Add a Collect Data action with the questions you want answered, such as company size or timeline. The agent asks them naturally during the conversation and saves the answers.',
        ),
      },
      {
        question: t('Where do my leads go?'),
        answer: t(
          'Every lead appears on the Leads page, where you can export them as CSV. You can also get an email for each new lead, or send them to a webhook.',
        ),
      },
      {
        question: t('Will it make up prices or discounts?'),
        answer: t(
          'It answers from the content you give it, and your instructions decide what it says when it isn’t sure. Keep your pricing page in its knowledge and retrain it when prices change.',
        ),
      },
      {
        question: t('Does it work on WhatsApp and the phone too?'),
        answer: t(
          'Yes. The same agent answers on your website, WhatsApp, Messenger, email and the phone, so leads can reach you wherever they like.',
        ),
      },
    ],
    finalExchange: {
      question: t('Is there a discount if we pay yearly?'),
      answer: t('Yes, you get two months free on any yearly plan.'),
    },
    finalTitle: t('Your agent could be booking demos by lunchtime.'),
  };
}

/** Free times on Thursday, as the agent offers them in the chat. */
function SlotPicker() {
  const { t } = useLocale('bot-use-cases');
  // Thursday afternoon (2 p.m., 3:30 p.m., 4:15 p.m.): translated so locales
  // that use a 24-hour clock match the time in the agent's reply.
  const slots = [t('2:00'), t('3:30'), t('4:15')];

  return (
    <div className='w-[85%] rounded-2xl bg-[#f6f6f9] p-3.5 ring-1 ring-black/[0.05] dark:bg-white/[0.05] dark:ring-white/[0.06]'>
      <p
        className={clsxm(
          'flex items-center gap-2 text-[13px] font-semibold',
          tone.ink,
        )}
      >
        <CalendarIcon className='h-4 w-4 flex-none text-[#c4501d] dark:text-[#f29a6b]' />
        {t('Thursday, 15-minute demo')}
      </p>
      <div className='mt-3 flex gap-2'>
        {slots.map((slot, index) => (
          <span
            key={slot}
            className={clsxm(
              'flex-1 rounded-xl py-2 text-center text-[13px] font-semibold tabular-nums',
              index === 1
                ? clsxm(BRAND_BG, 'text-white')
                : clsxm(
                    'bg-white ring-1 ring-black/[0.08] dark:bg-white/[0.06] dark:ring-white/10',
                    tone.ink,
                  ),
            )}
          >
            {slot}
          </span>
        ))}
      </div>
    </div>
  );
}
